import React from 'react';
// Native MUI Components
import { 
  Card, 
  CardContent, 
  Typography, 
  Chip, 
  Box, 
  Grid, 
  Divider,
  Stack
} from '@mui/material';
import { BillData } from '@/types';
import { useI18n } from '@/hooks/useI18n';

interface BillCardsProps {
  billData: BillData[];
}

const BillCards: React.FC<BillCardsProps> = ({ billData }) => {
  const { t } = useI18n();
  
  const getBillStatusColor = (status: string) => {
    switch (status) {
      case 'not_sent': return { bgcolor: 'grey.100', color: 'grey.800' };
      case 'initial_payment': return { bgcolor: 'info.50', color: 'info.800' };
      case 'payment_requested': return { bgcolor: 'warning.50', color: 'warning.800' };
      case 'full_payment': return { bgcolor: 'success.50', color: 'success.800' };
      default: return { bgcolor: 'grey.100', color: 'grey.800' };
    }
  };

  const getBillStatusLabel = (status: string) => {
    switch (status) {
      case 'not_sent': return t('bill.status.notSent');
      case 'initial_payment': return t('bill.status.initialPayment');
      case 'payment_requested': return t('bill.status.paymentRequested');
      case 'full_payment': return t('bill.status.fullPayment');
      default: return status;
    }
  };

  return (
    <Grid container spacing={3}>
      {billData.map((bill) => (
        <Grid size={{ xs: 12, md: 6, lg: 4 }} key={bill.apartmentId}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" component="h3">
                  {bill.apartmentName}
                </Typography>
                <Chip 
                  label={getBillStatusLabel(bill.billStatus)}
                  size="small"
                  sx={getBillStatusColor(bill.billStatus)}
                />
              </Box>
              
              <Stack spacing={1}>
                {bill.travellers.map((traveller, index) => (
                  <Box key={index}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', pb: 1 }}>
                      <Typography variant="body2">
                        {traveller.name}
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        € {traveller.total}
                      </Typography>
                    </Box>
                    {index < bill.travellers.length - 1 && <Divider />}
                  </Box>
                ))}
                
                <Box sx={{ pt: 2, mt: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6" component="span">
                      {t('bill.table.total')}:
                    </Typography>
                    <Typography variant="h6" component="span">
                      € {bill.grandTotal}
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default BillCards;
